import React, { useState } from 'react';
import { Mail, Linkedin, MapPin, Send, MessageSquare, CheckCircle, X } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // EmailJS configuration
      const serviceId = 'service_fy5z4ki';
      const templateId = 'template_td411lj';
      const publicKey = 'H9xoSZwakhjdoeLJG';

      // Template parameters for EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_name: 'Swastik Nanda',
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      // Show success popup
      setIsSubmitting(false);
      setShowSuccessPopup(true);
      setFormData({ name: '', email: '', subject: '', message: '' });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setIsSubmitting(false);
      setShowErrorPopup(true);
    }
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      value: "swastik.nanda@gmail.com",
      link: "mailto:swastik.nanda@gmail.com",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: <Linkedin className="w-6 h-6" />,
      title: "LinkedIn",
      value: "Connect with me",
      link: "https://www.linkedin.com/in/swastik-nanda-gen-ai?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B9qf5H69rRCaxYGsmxyUf1w%3D%3D",
      color: "from-blue-600 to-blue-800"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Location",
      value: "Bhubaneswar, India",
      link: "#",
      color: "from-green-400 to-green-600"
    }
  ];

  return (
    <>
      <section id="contact" className="py-20 bg-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get In <span className="text-blue-400">Touch</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Ready to collaborate on innovative tech solutions? Let's discuss how we can work together to create something extraordinary
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-6">
                  Let's Connect
                </h3>
                <p className="text-gray-400 leading-relaxed mb-8">
                  I'm always excited to discuss new opportunities, innovative projects, and potential collaborations. 
                  Whether you're looking for technical expertise, want to explore AI solutions, or simply want to connect 
                  with a fellow tech enthusiast, I'd love to hear from you.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="grid sm:grid-cols-3 gap-4">
                {contactInfo.map((info, index) => (
                  <a
                    key={index}
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10 group block"
                  >
                    <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${info.color} mb-4`}>
                      <div className="text-white">
                        {info.icon}
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                      {info.title}
                    </h4>
                    <p className="text-gray-400 text-sm">
                      {info.value}
                    </p>
                  </a>
                ))}
              </div>

              {/* Quick Stats */}
              <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-blue-400" />
                  Quick Facts
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Response Time</span>
                    <span className="text-blue-400 font-medium">Within 24 hours</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Preferred Contact</span>
                    <span className="text-blue-400 font-medium">Email or LinkedIn</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Availability</span>
                    <span className="text-green-400 font-medium">Open to opportunities</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-8">
              <h3 className="text-2xl font-semibold text-white mb-6">
                Send a Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                    placeholder="What's this about?"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 resize-vertical"
                    placeholder="Tell me about your project, idea, or just say hello..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 flex items-center justify-center ${
                    isSubmitting 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                  } text-white px-8 py-4 rounded-lg font-semibold`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-8">
              <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">
                Ready to Build Something <span className="text-blue-400">Amazing</span>?
              </h3>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                From automation solutions to AI-powered applications, let's collaborate on projects that make a real impact. 
                I bring 6+ years of experience and a passion for innovative problem-solving to every project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:swastik.nanda@gmail.com"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email Me Directly
                </a>
                <a
                  href="https://www.linkedin.com/in/swastik-nanda-gen-ai?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B9qf5H69rRCaxYGsmxyUf1w%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
                >
                  <Linkedin className="w-5 h-5 mr-2" />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg mx-auto relative animate-in zoom-in duration-300 max-h-[95vh] sm:max-h-[90vh] lg:max-h-[85vh] flex flex-col">
            {/* Header - Fixed */}
            <div className="flex-shrink-0 p-4 sm:p-6 pb-3">
              {/* Close button */}
              <button
                onClick={closeSuccessPopup}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors duration-300 z-10"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Success icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              
              {/* Title */}
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  Message Sent Successfully! 🎉
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-2">
              <div className="text-center">
                <p className="text-gray-300 mb-6 leading-relaxed text-sm sm:text-base">
                  Thank you for reaching out! Your message has been successfully sent to my email. 
                  I typically respond within 24 hours and look forward to connecting with you soon.
                </p>
                
                <div className="space-y-3 text-sm text-gray-400 mb-6">
                  <p className="flex items-center justify-center">
                    <Mail className="w-4 h-4 mr-2 text-blue-400" />
                    Message sent to: swastik.nanda@gmail.com
                  </p>
                  <p className="bg-slate-700/50 rounded-lg p-3 border border-green-500/20">
                    <span className="text-green-400 font-medium">✨ Pro Tip:</span> 
                    Feel free to also connect with me on LinkedIn for quicker responses!
                  </p>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex-shrink-0 p-3 sm:p-4 pt-3 border-t border-slate-600/50">
              <div className="flex flex-col gap-2 sm:gap-3 mb-3">
                <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={closeSuccessPopup}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm min-h-[44px]"
                  >
                    Perfect, Got it!
                  </button>
                  <a
                    href="https://www.linkedin.com/in/swastik-nanda-gen-ai?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B9qf5H69rRCaxYGsmxyUf1w%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 text-center flex items-center justify-center text-xs sm:text-sm min-h-[44px]"
                  >
                    <Linkedin className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span className="truncate">Connect on LinkedIn</span>
                  </a>
                </div>
              </div>

              <div className="text-center">
                <p className="text-gray-400 text-xs leading-relaxed">
                  Thank you for connecting! I look forward to our conversation. 🙏
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg mx-auto relative animate-in zoom-in duration-300 flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 p-4 sm:p-6 pb-3">
              {/* Close button */}
              <button
                onClick={closeErrorPopup}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors duration-300 z-10"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Error icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <X className="w-8 h-8 text-red-400" />
                </div>
              </div>
              
              {/* Title */}
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  Message Failed to Send
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed text-sm sm:text-base">
                  Sorry, there was an issue sending your message. Please try again or contact me directly via email or LinkedIn.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex-shrink-0 p-3 sm:p-4 pt-3 border-t border-slate-600/50">
              <div className="flex flex-col gap-2 sm:gap-3">
                <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
                  <a
                    href="mailto:swastik.nanda@gmail.com"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center text-xs sm:text-sm min-h-[44px]"
                  >
                    <Mail className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span className="truncate">Send Email</span>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/swastik-nanda-gen-ai?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B9qf5H69rRCaxYGsmxyUf1w%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 text-center flex items-center justify-center text-xs sm:text-sm min-h-[44px]"
                  >
                    <Linkedin className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span className="truncate">LinkedIn</span>
                  </a>
                </div>
                
                <button
                  onClick={closeErrorPopup}
                  className="w-full bg-slate-600 hover:bg-slate-500 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm min-h-[44px]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Contact;